import { registerDefaultsForModule, ComposedTheme, hexToRGBArray } from '@glu/theming';

// eslint-disable-next-line import/prefer-default-export
export const dataComponentsTheme = ({ palette, typography }: ComposedTheme) => ({
  drawer: {
    footer: {
      // @theming labelText: Footer background color, type: color
      backgroundColor: palette.primary.darkBackground,
      cancelButton: {
        // @theming labelText: Footer cancel button color, type: color, backgroundColor: dataComponents.drawer.footer.backgroundColor
        color: palette.text.light,
      },
    },
    // @theming labelText: Drawer background color, type: color
    backgroundColor: palette.primary.darkBackground,
    text: {
      font: {
        // @theming labelText: Drawer input autofill font family, type: fontFamily
        fontFamily: typography.fontFamily,
      },
      // @theming labelText: Drawer input fill color, type: color
      color: palette.text.light,
    },
    // @theming labelText: Close icon color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
    closeIconColor: palette.background,
    // @theming labelText: Tab color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
    tabColor: palette.text.light,
    // @theming labelText: Scrollbar background color, type: color
    scrollbarBackground: palette.primary.darkBackground,
    // @theming labelText: Scrollbar color, type: string
    scrollbarColor: `rgb(${hexToRGBArray(palette.primary.darkBackground)!
      .map((a) => a - 30)
      .join(',')})`,
    filters: {
      hidden: {
        // @theming labelText: Filters hidden font family, type: fontFamily
        fontFamily: typography.label.fontFamily,
        // @theming labelText: Filters hidden font size, type: pixel
        fontSize: typography.label.fontSize,
        // @theming labelText: Filters hidden font weight, type: fontWeight
        fontWeight: typography.label.fontWeight,
        // @theming labelText: Filters hidden transform, type: string
        textTransform: typography.label.textTransform,
        // @theming labelText: Filters hidden text color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
        color: palette.accent,
      },
      // @theming labelText: Filters clear button text color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
      clearButtonTextColor: palette.text.light,
      text: {
        // @theming labelText: Filters text font family, type: fontFamily
        fontFamily: typography.text.fontFamily,
        // @theming labelText: Filters text font size, type: pixel
        fontSize: typography.text.fontSize,
        // @theming labelText: Filters text font weight, type: fontWeight
        fontWeight: typography.text.fontWeight,
        // @theming labelText: Filters text color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
        color: palette.text.light,
      },
    },
    columns: {
      // @theming labelText: Columns item hover color, type: string
      itemHoverColor: `rgb(${hexToRGBArray(palette.primary.darkBackground)!
        .map((a) => a + 30)
        .join(',')})`,
      header: {
        font: {
          // @theming labelText: Columns header font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Columns header font size, type: pixel
          fontSize: typography.text.fontSize,
          // @theming labelText: Columns header font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Columns header text transform, type: string
          textTransform: 'uppercase',
        },
      },
      text: {
        font: {
          // @theming labelText: Columns font family, type: fontFamily
          fontFamily: typography.fontFamily,
        },
        // @theming labelText: Columns text color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
        color: palette.text.light,
      },
      maxPrimary: {
        font: {
          // @theming labelText: Columns max font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Columns max font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Columns max font size, type: pixel
          fontSize: 12,
          // @theming labelText: Columns max font style, type: string
          fontStyle: 'italic',
        },
        // @theming labelText: Columns max color, type: color, backgroundColor: dataComponents.drawer.backgroundColor
        color: palette.text.light,
      },
      lockedColumn: {
        font: {
          // @theming labelText: Columns item font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Columns item font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // isn't being used for locked columns, is used for all column items
          // @theming labelText: Columns item font size, type: pixel
          fontSize: 14,
        },
        // @theming labelText: Columns locked icon color, type: color
        lockIconColor: palette.text.light,
      },
      // @theming labelText: Columns drag icon color, type: color
      dragIconColor: palette.text.light,
      // @theming labelText: Columns pin text color, type: color
      pinButtonTextColor: palette.text.light,
      // @theming labelText: Columns unpin text color, type: color
      unpinAllButtonTextColor: palette.text.light,
    },
  },
  exportIcon: {
    menuButton: {
      text: {
        font: {
          // @theming labelText: Menu item font family, type: fontFamily
          fontFamily: typography.fontFamily,
        },
        // @theming labelText: Menu item color, type: color
        color: palette.text.dark,
      },
      // @theming labelText: Menu item hover background color, type: color
      hoverColor: palette.highlightBackground,
    },
  },
  filterChips: {
    label: {
      text: {
        // @theming labelText: filter chips font family, type: fontFamily
        fontFamily: typography.label.fontFamily,
        // @theming labelText: filter chips color, type: color
        color: typography.label.color,
        // @theming labelText: filter chips font size, type: pixel
        fontSize: typography.label.fontSize,
        // @theming labelText: filter chips font weight, type: fontWeight
        fontWeight: typography.label.fontWeight,
      },
    },
    groupLabel: {
      font: {
        // @theming labelText: Group label font family, type: fontFamily
        fontFamily: typography.label.fontFamily,
        // @theming labelText: Group label color, type: color
        color: typography.label.color,
        // @theming labelText: Group label font weight, type: fontWeight
        fontWeight: typography.label.fontWeight,
        // @theming labelText: Group label font size, type: pixel
        fontSize: 10,
      },
    },
    chip: {
      text: {
        font: {
          // @theming labelText: Selectable filter chip font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Selectable filter chip font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Selectable filter chip font size, type: pixel
          fontSize: typography.label.fontSize,
        },
        // @theming labelText: Selectable filter chip color, type: color
        color: palette.text.light,
      },
    },
    count: {
      text: {
        font: {
          // @theming labelText: Count text font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Count text font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText:Count font size, type: pixel
          fontSize: 14,
        },
        // @theming labelText: Count color, type: color
        color: palette.primary.normal,
      },
    },
  },
  pagination: {
    text: {
      // @theming labelText: Pagination text font family, type: fontFamily
      fontFamily: typography.text.fontFamily,
      // @theming labelText: Pagination text font size, type: pixel
      fontSize: typography.text.fontSize,
      // @theming labelText: Pagination text font weight, type: fontWeight
      fontWeight: typography.text.fontWeight,
    },
    rangeText: {
      font: {
        // @theming labelText: Range text color, type: color
        color: typography.label.color,
        // @theming labelText: Range font family, type: fontFamily
        fontFamily: typography.label.fontFamily,
        // @theming labelText: Range font size, type: pixel
        fontSize: typography.label.fontSize,
        // @theming labelText: Range font weight, type: string
        fontWeight: typography.fontWeightRegular,
      },
      // @theming labelText: Range color, type: color
      color: palette.text.dark,
    },
    activePageText: {
      font: {
        // @theming labelText: Active page font weight, type: string
        fontWeight: typography.fontWeightHeavy,
      },
    },
    // @theming labelText: Range ellipsis color, type: color
    pageEllipsisColor: palette.text.dark,
  },
  footer: {
    // @theming labelText: Background color, type: color
    backgroundColor: palette.background,
    // @theming labelText: Box shadow, type: string
    boxShadow: '0px -16px 11px -15px rgba(0, 0, 0, 0.3)',
  },
  rowActions: {
    menuButton: {
      text: {
        font: {
          // @theming labelText: Font family, type: fontFamily
          fontFamily: typography.fontFamily,
        },
        // @theming labelText: Other actions color, type: color
        color: palette.text.dark,
      },
      // @theming labelText: Other actions hover color, type: color
      hoverColor: palette.highlightBackground,
    },
    // @theming labelText: Actions separator color, type: color
    firstActionBorderColor: palette.accent,
  },
  savedViews: {
    error: {
      // @theming labelText: Error icon color, type: color
      iconColor: palette.form.error,
      text: {
        font: {
          // @theming labelText: Error Font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Error Font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Error font size, type: pixel
          fontSize: 12,
        },
        // @theming labelText: Error message color, type: color
        color: palette.form.error,
      },
      // @theming labelText: Save error close icon color, type: color
      closeIconColor: palette.text.midGray,
    },
    text: {
      font: {
        // @theming labelText: Active view name Font family, type: fontFamily
        fontFamily: typography.text.fontFamily,
        // @theming labelText: Active view name Font weight, type: fontWeight
        fontWeight: typography.text.fontWeight,
        // @theming labelText: Active view name text transform, type: string
        textTransform: 'uppercase',
        // @theming labelText: Active view name font size, type: pixel
        fontSize: 14,
      },
      // @theming labelText: Active view name color, type: color
      color: palette.text.dark,
    },
    delete: {
      text: {
        // @theming labelText: Delete message  Font family, type: fontFamily
        fontFamily: typography.text.fontFamily,
        // @theming labelText: Delete message  Font weight, type: fontWeight
        fontWeight: typography.text.fontWeight,
        // @theming labelText: Delete message font size, type: pixel
        fontSize: 14,
        // @theming labelText: Delete message font style, type: string
        fontStyle: 'italic',
      },
    },
    // @theming labelText: Changed view icon color, type: color
    divergedIconColor: palette.primary.light,
    // @theming labelText: Delete warning icon color, type: color
    deleteWarningIconColor: palette.colors.yellowOrange,
    changed: {
      text: {
        font: {
          // @theming labelText: Changed text font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Changed text font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Changed text font size, type: pixel
          fontSize: 14,
          // @theming labelText: Changed text font style, type: string
          fontStyle: 'italic',
        },
        // @theming labelText: Changed text color, type: color
        color: palette.text.midGray,
      },
      // @theming labelText: Message separator color, type: color
      borderColor: palette.accent,
    },
    saved: {
      // @theming labelText: Saved text icon color, type: color
      checkIconColor: palette.info,
      text: {
        font: {
          // @theming labelText: Saved text font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Saved text font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Saved text font size, type: pixel
          fontSize: 14,
          // @theming labelText: Saved text font style, type: string
          fontStyle: 'italic',
        },
        // @theming labelText: Saved text color, type: color
        color: palette.text.dark,
      },
    },
    group: {
      text: {
        // @theming labelText: Group text font family, type: fontFamily
        fontFamily: typography.label.fontFamily,
        // @theming labelText: Group text color, type: color
        color: typography.label.color,
        // @theming labelText: Group text font size, type: pixel
        fontSize: typography.label.fontSize,
        // @theming labelText: Group text font weight, type: fontWeight
        fontWeight: typography.label.fontWeight,
        // @theming labelText: Group text transform , type: string
        textTransform: typography.label.textTransform,
      },
      empty: {
        text: {
          // @theming labelText: Group empty content font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Group empty content font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Group empty content font size, type: pixel
          fontSize: typography.text.fontSize,
          // @theming labelText: Group empty content color, type: color
          color: palette.text.dark,
        },
      },
      // @theming labelText: Group header border color, type: color
      borderColor: palette.accent,
    },
    menu: {
      // @theming labelText: Menu item hover color, type: color
      hoverColor: palette.highlightBackground,
      text: {
        font: {
          // @theming labelText: Menu item font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Menu item font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Menu item font size, type: pixel
          fontSize: 14,
        },
        // @theming labelText: Menu item color, type: color
        color: palette.text.dark,
      },
      default: {
        font: {
          // @theming labelText: Default text font family, type: fontFamily
          fontFamily: typography.text.fontFamily,
          // @theming labelText: Default text font weight, type: fontWeight
          fontWeight: typography.text.fontWeight,
          // @theming labelText: Default text font size, type: pixel
          fontSize: 12,
          // @theming labelText: Default text font style, type: string
          fontStyle: 'italic',
        },
        // @theming labelText: Default text color, type: color
        color: palette.text.midGray,
      },
      // @theming labelText: Rename/delete options separator color, type: color
      noBorderColor: palette.accent,
    },
  },
  controlBar: {
    // @theming labelText: Icon separator color, type: color
    separatorColor: palette.accent,
  },
  search: {
    text: {
      font: typography.text,
    },
  },
});

registerDefaultsForModule('dataComponents', dataComponentsTheme);
